import type { MouseEventHandler, ReactNode } from "react";
import { Box, Paper, type PaperProps } from "@mui/material";

declare module "@mui/material/Autocomplete" {
	interface AutocompletePaperSlotPropsOverrides {
		pane?: ReactNode;
		onPaneMouseDown?: MouseEventHandler;
		hint?: ReactNode;
	}
}

const LIST_WIDTH = 480;
const CARD_WIDTH = 280;
const CARD_GAP = 8;

/** Wide enough for the list and the card beside it. */
export const PANE_DROPDOWN_WIDTH = LIST_WIDTH + CARD_GAP + CARD_WIDTH;

interface JobLookupDropdownPaperProps extends PaperProps {
	pane?: ReactNode;
	onPaneMouseDown?: MouseEventHandler;
	hint?: ReactNode;
}

/** The dropdown's surface. A `pane` stands apart from the list as a floating card of its own, outside the listbox, whose
 *  children may only be options. The list keeps its own scrollbar, inside its own surface, and a `hint` sits under it
 *  there, as long as the list is what has a pane beside it: `onPaneMouseDown` is what keeps a press on it from blurring
 *  the box. */
export function JobLookupDropdownPaper({
	children,
	pane,
	onPaneMouseDown,
	hint,
	...paper
}: JobLookupDropdownPaperProps) {
	if (onPaneMouseDown === undefined) return <Paper {...paper}>{children}</Paper>;
	return (
		<Box
			onMouseDown={onPaneMouseDown}
			sx={{
				display: "grid",
				gridTemplateColumns: `minmax(0, 1fr) ${CARD_WIDTH}px`,
				gap: `${CARD_GAP}px`,
				alignItems: "start",
			}}
		>
			<Paper {...paper}>
				{children}
				{hint}
			</Paper>
			{pane && <Paper variant="floating">{pane}</Paper>}
		</Box>
	);
}
